/* eslint-disable max-lines */
import Immutable from 'immutable';
import { createStructuredSelector } from 'reselect';
import { StatusCodes } from 'http-status-codes';

import logger from 'logger';
import { translate } from 'services/i18nService';
import ApiService from 'services/ApiService/ApiService';
import NotificationService from 'services/NotificationService';
import TwoFaService from 'services/TwoFaService';
import APIStates from 'enum/APIStates';
import { ENUM_PAYMENT_PROCESSOR_IDS } from 'enum/payments';
import { ENUM_TWO_FA } from 'enum/profile';
import createMessagingInstance from 'services/MessagingService/MessagingService';
import { BUY_TOKENS_MODAL_SHOWN as BUY_TOKENS_MODAL_SHOWN_EVENT_NAME } from 'services/MessagingService/EventNames';
import { MONTH_AS_DAYS } from 'enum/timeFormat';
import {
  getApiWarnMsg,
  isValidResponse,
  usernameOrImpersonate,
  calcPricePerMonth,
} from 'utils/helpers';
import makeAction from 'utils/makeAction';
import parseCatch from 'utils/parseCatch';

import {
  CREATE_AUTOPAYMENT_URL,
  DELETE_AUTOPAYMENT_URL,
  GET_AUTOPAYMENT_DATA_URL,
  GOLD_MEMBERSHIPS_URL,
  PAYMENT_DELETE_PROCESSOR_URL,
  PAYMENT_LINK_PROCESSOR_URL,
  PAYMENT_METHODS_URL,
  PAYMENT_PROCESSORS_URL,
  PAYMENT_UPDATE_PROCESSOR_URL,
  UPDATE_AUTOPAYMENT_URL,
} from './Api';


const CREATED_AUTOPAYMENT = 'cam4/payment/CREATED_AUTOPAYMENT';
const DELETED_AUTOPAYMENT = 'cam4/payment/DELETED_AUTOPAYMENT';
const FETCH_PAYMENT_METHODS = 'cam4/payment/FETCH_PAYMENT_METHODS';
const HIDE_BUY_TOKENS_MODAL = 'cam4/payment/HIDE_BUY_TOKENS_MODAL';
const HIDE_UPGRADE_TO_GOLD_MODAL = 'cam4/payment/HIDE_UPGRADE_TO_GOLD_MODAL';
const INIT_FETCH_MEMBERSHIPS = 'cam4/payment/INIT_FETCH_MEMBERSHIPS';
const INIT_FETCH_PAYMENT_METHODS = 'cam4/payment/INIT_FETCH_PAYMENT_METHODS';
const IS_FETCHING_PAYMENT_METHODS = 'cam4/payment/IS_FETCHING_PAYMENT_METHODS';
const SET_AUTOPAYMENT_DATA = 'cam4/payment/SET_AUTOPAYMENT_DATA';
const SET_DEFAULT_PAYMENT_PROCESSOR = 'cam4/payment/SET_DEFAULT_PAYMENT_PROCESSOR';
const SET_GOLD_MEMBERSHIPS = 'cam4/payment/SET_GOLD_MEMBERSHIPS';
const SET_MEMBERSHIP_OPTIONS = 'cam4/payment/SET_MEMBERSHIP_OPTIONS';
const SET_PAYMENT_METHODS = 'cam4/payment/SET_PAYMENT_METHODS';
const SET_PAYMENT_PROCESSORS = 'cam4/payment/SET_PAYMENT_PROCESSORS';
const SHOW_BUY_TOKENS_MODAL = 'cam4/payment/SHOW_BUY_TOKENS_MODAL';
const SHOW_UPGRADE_TO_GOLD_MODAL = 'cam4/payment/SHOW_UPGRADE_TO_GOLD_MODAL';
const TOGGLE_REGISTER_GOLD_OVERLAY = 'cam4/payment/TOGGLE_REGISTER_GOLD_OVERLAY';
const TOGGLE_UPSELL_MODAL = 'cam4/payment/TOGGLE_UPSELL_MODAL';
const UPDATED_AUTOPAYMENT = 'cam4/payment/UPDATED_AUTOPAYMENT';
const UPDATE_LINKING_STATUS = 'cam4/payment/UPDATE_LINKING_STATUS';
const UPDATE_PAYMENT_PROCESSOR_FIELDS = 'cam4/payment/UPDATE_PAYMENT_PROCESSOR_FIELDS';
const UPDATE_SOCIALE = 'cam4/payment/UPDATE_SOCIALE';

export const NAME = 'payment';

const messagingInstance = createMessagingInstance();

const initialState = Immutable.fromJS({
  products: [],
  memberships: [],
  isFetchingProducts: false,
  isFetchingMemberships: false,
  isFetchingPayementMethods: false,
  isRegisterGoldOverlayVisible: false,
  isUpsellModalVisible: false,
  paymentTypes: {},
  isBuyTokensModalVisible: false,
  isUpgradeToGoldModalVisible: false,
  payouts: { processors: [] },
  sociale: {},
  requests: [],
  membershipOptions: {
    selectedMembership: {},
    modalSize: 'medium',
    step: 0,
  },
  autoPayment: null,
  linkingStatus: APIStates.INITIAL,
});

// eslint-disable-next-line complexity
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_PAYMENT_METHODS:
      return state
        .set('products', Immutable.fromJS(action.products))
        .set('paymentTypes', Immutable.fromJS(action.paymentTypes))
        .set('isFetchingProducts', false);
    case SET_GOLD_MEMBERSHIPS:
      return state
        .set('products', Immutable.fromJS(action.products))
        .set('memberships', Immutable.fromJS(action.memberships))
        .set('isFetchingMemberships', false);
    case INIT_FETCH_PAYMENT_METHODS:
      return state.set('isFetchingProducts', true);
    case TOGGLE_REGISTER_GOLD_OVERLAY:
      return state.set('isRegisterGoldOverlayVisible', action.isVisible);
    case TOGGLE_UPSELL_MODAL:
      return state.set('isUpsellModalVisible', action.isVisible);
    case INIT_FETCH_MEMBERSHIPS:
      return state.set('isFetchingMemberships', true);
    case IS_FETCHING_PAYMENT_METHODS:
      return state.set('isFetchingPayementMethods', action.isFetchingPayementMethods);
    case SHOW_BUY_TOKENS_MODAL:
      messagingInstance.emit(BUY_TOKENS_MODAL_SHOWN_EVENT_NAME);

      return state.set('isBuyTokensModalVisible', true);
    case SHOW_UPGRADE_TO_GOLD_MODAL:
      return state.set('isUpgradeToGoldModalVisible', true);
    case HIDE_BUY_TOKENS_MODAL:
      return state.set('isBuyTokensModalVisible', false);
    case HIDE_UPGRADE_TO_GOLD_MODAL:
      return (
        state
          .set('isUpgradeToGoldModalVisible', false)
          // .set('products', Immutable.fromJS({}))
          .set('memberships', Immutable.fromJS({}))
      );
    case SET_PAYMENT_PROCESSORS:
      return state.set('payouts', Immutable.fromJS(action.payouts));
    case SET_DEFAULT_PAYMENT_PROCESSOR:
      return state.setIn(['payouts', 'activeProcessorId'], action.processorId);
    case UPDATE_PAYMENT_PROCESSOR_FIELDS: {
      const processorsList = state.getIn(['payouts', 'processors']);
      const index = processorsList.findIndex((processor) => processor.get('processorId') === action.processorId);
      const newList = processorsList.update(index, (item) =>
        item.set('fields', Immutable.fromJS(action.processorFields))
      );

      return state.setIn(['payouts', 'processors'], newList);
    }
    case SET_AUTOPAYMENT_DATA:
      return state.set('autoPayment', Immutable.fromJS(action.autoPayment));
    case CREATED_AUTOPAYMENT:
      return state.set('autoPayment', Immutable.fromJS(action.autoPayment));
    case DELETED_AUTOPAYMENT:
      return state.set('autoPayment', Immutable.fromJS(action.autoPayment));
    case UPDATED_AUTOPAYMENT:
      return state.set('autoPayment', Immutable.fromJS(action.autoPayment));
    case SET_MEMBERSHIP_OPTIONS:
      return state.set('membershipOptions', Immutable.fromJS(action.membershipOptions));
    case UPDATE_SOCIALE:
      return state.set('sociale', Immutable.fromJS(action.sociale));
    case UPDATE_LINKING_STATUS: {
      return state.set('linkingStatus', action.linkingStatus);
    }
    default:
      return state;
  }
}

// SELECTORS
const products = (state) => state[NAME].get('products');
const memberships = (state) => state[NAME].get('memberships');
const isFetchingProducts = (state) => state[NAME].get('isFetchingProducts');
const isFetchingMemberships = (state) => state[NAME].get('isFetchingMemberships');
const isFetchingPayementMethods = (state) => state[NAME].get('isFetchingPayementMethods');
const isRegisterGoldOverlayVisible = (state) => state[NAME].get('isRegisterGoldOverlayVisible');
const isUpsellModalVisible = (state) => state[NAME].get('isUpsellModalVisible');
const paymentTypes = (state) => state[NAME].get('paymentTypes');
const isBuyTokensModalVisible = (state) => state[NAME].get('isBuyTokensModalVisible');
const isUpgradeToGoldModalVisible = (state) => state[NAME].get('isUpgradeToGoldModalVisible');
const payouts = (state) => state[NAME].get('payouts');
const sociale = (state) => state[NAME].get('sociale');
const requests = (state) => state[NAME].get('requests');
const autoPayment = (state) => state[NAME].get('autoPayment');
const membershipOptions = (state) => state[NAME].get('membershipOptions');
const linkingStatus = (state) => state[NAME].get('linkingStatus');

// ACTIONS
const showBuyTokensModal = makeAction(SHOW_BUY_TOKENS_MODAL);
const showUpgradeToGoldModal = makeAction(SHOW_UPGRADE_TO_GOLD_MODAL);
const hideBuyTokensModal = makeAction(HIDE_BUY_TOKENS_MODAL);
const hideUpgradeToGoldModal = makeAction(HIDE_UPGRADE_TO_GOLD_MODAL);
const setProcessors = makeAction(SET_PAYMENT_PROCESSORS, 'payouts');
const changeDefaultProcessor = makeAction(SET_DEFAULT_PAYMENT_PROCESSOR, 'processorId');
const updateProcessorFields = makeAction(UPDATE_PAYMENT_PROCESSOR_FIELDS, 'processorId', 'processorFields');
const fetchPaymentMethodsAction = makeAction(FETCH_PAYMENT_METHODS);
const setGoldMemberships = makeAction(SET_GOLD_MEMBERSHIPS, 'memberships', 'products');
const toggleRegisterGoldOverlay = makeAction(TOGGLE_REGISTER_GOLD_OVERLAY, 'isVisible');
const toggleUpsellModal = makeAction(TOGGLE_UPSELL_MODAL, 'isVisible');
const updateIsFetchingPayementMethods = makeAction(IS_FETCHING_PAYMENT_METHODS, 'isFetchingPayementMethods');
const setMembershipOptions = makeAction(SET_MEMBERSHIP_OPTIONS, 'membershipOptions');
const setAutopaymentData = makeAction(SET_AUTOPAYMENT_DATA, 'autoPayment');
const createdAutopayment = makeAction(CREATED_AUTOPAYMENT, 'autoPayment');
const deletedAutopayment = makeAction(DELETED_AUTOPAYMENT, 'autoPayment');
const updatedAutopayment = makeAction(UPDATED_AUTOPAYMENT, 'autoPayment');
const updateSociale = makeAction(UPDATE_SOCIALE, 'sociale');
const updateLinkingStatus = makeAction(UPDATE_LINKING_STATUS, 'linkingStatus');

function initFetchMemberships() {
  return { type: INIT_FETCH_MEMBERSHIPS };
}

function fetchGoldMemberships(language, username, accessHash) {
  return (dispatch) => {
    const url = ApiService.substitute(GOLD_MEMBERSHIPS_URL, {
      language,
      username: username || '',
      accessHash: accessHash || '',
    });

    dispatch(initFetchMemberships());

    ApiService.get(encodeURI(url), { withCredentials: false })
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          const products = [];
          res.data.products.forEach((item) => {
            const product = {
              id: item.id,
              currency: item.currency.symbol,
              recurringPeriod: item.details.recurringPeriod,
              amount: item.amount,
              price: calcPricePerMonth(item),
              months: parseInt(item.details.recurringPeriod / MONTH_AS_DAYS, 10),
              item,
            };
            products.push(product);
          });
          dispatch(setGoldMemberships(products.reverse(), res.data.products));
        }
      });
  };
}

function setPaymentMethods(products, paymentTypes) {
  return {
    type: SET_PAYMENT_METHODS,
    products,
    paymentTypes,
  };
}

function fetchPaymentMethods(language, username, accessHash) {
  return (dispatch) => {
    dispatch(fetchPaymentMethodsAction());

    const url = ApiService.substitute(PAYMENT_METHODS_URL, {
      language,
      username: username || '',
      accessHash: accessHash || '',
    });

    ApiService.get(encodeURI(url), { withCredentials: false }).then(
      (res) => {
        if (res.status === StatusCodes.OK) {
          const paymentTypes = res.data.products.reduce((acc, item) => {
            const types = item.categories.reduce(
              (arr, cat) =>
                arr.concat(cat.methods.map((method) => {
                  return { method: method.type, processorId: method.processorId };
                })),
              []
            );
            types.forEach((type) => {
              if (acc[type.method]) {
                acc[type.method].products.push(item);
              } else {
                acc[type.method] = {
                  products: [],
                  processorId: type.processorId,
                };
              }
            });

            return acc;
          }, {});
          dispatch(setPaymentMethods(res.data.products, paymentTypes));
        }
      },
      (error) => {
        parseCatch(error);
      }
    );
  };
}

// function initFetchPaymentMethods() {
//   return {
//     type: INIT_FETCH_PAYMENT_METHODS
//   };
// }

function getPaymentProcessors(userName, accessHash, lastCashOut = false, status = '') {
  return (dispatch) => {
    const url = PAYMENT_PROCESSORS_URL;
    const params = {};
    const headers = {
      Username: userName,
      'Access-Hash': accessHash,
    };
    if (lastCashOut) {
      params.lastCashOut = true;
      headers.Jwt = accessHash;
    }
    if (status) {
      params.status = status;
    }
    if (usernameOrImpersonate()) {
      params.userName = usernameOrImpersonate(userName);
    }
    dispatch(updateIsFetchingPayementMethods(true));

    ApiService.get(url, {
      params,
      headers,
      withCredentials: false,
    })
      .then((res) => {
        dispatch(updateLinkingStatus(APIStates.INITIAL));
        if (isValidResponse(res)) {
          dispatch(updateIsFetchingPayementMethods(false));
          dispatch(setProcessors(res.data));
        } else {
          logger.warn(getApiWarnMsg('getPaymentProcessors', res.data));
          dispatch(updateIsFetchingPayementMethods(false));
        }
      })
      .catch((err) => {
        dispatch(updateLinkingStatus(APIStates.INITIAL));
        dispatch(updateIsFetchingPayementMethods(false));
        parseCatch(err);
      });
  };
}

function getAutoPaymentData(userName, accessHash) {
  return (dispatch) => {
    const url = encodeURI(GET_AUTOPAYMENT_DATA_URL);
    ApiService.get(url, {
      headers: {
        Username: userName,
        'Access-Hash': accessHash,
      },
      withCredentials: false,
    })
      .then((res) => {
        if (isValidResponse(res)) {
          dispatch(setAutopaymentData(res.data));
        }
      })
      .catch((err) => {
        parseCatch(err);
      });
  };
}

function createAutopayment(data, username, accessHash, successMessage) {
  return (dispatch) => {
    ApiService.post(CREATE_AUTOPAYMENT_URL, {
      data,
      headers: {
        Username: username,
        'Access-Hash': accessHash,
      },
      withCredentials: false,
    })
      .then((res) => {
        if (isValidResponse(res)) {
          dispatch(createdAutopayment(res.data));
          if (successMessage) {
            NotificationService.create({
              type: NotificationService.type.TOAST,
              subtype: NotificationService.subtype.SUCCESS,
              text: successMessage,
            });
          }
        }
      })
      .catch((err) => parseCatch(err));
  };
}

function deleteAutopayment(autoPaymentId, username, accessHash, successMessage) {
  return (dispatch) => {
    const url = ApiService.substitute(DELETE_AUTOPAYMENT_URL, { autoPaymentId });
    const headers = {
      Username: username,
      'Access-Hash': accessHash,
    };

    return ApiService.delete(encodeURI(url), {
      headers,
      withCredentials: false,
    })
      .then(() => {
        dispatch(deletedAutopayment(null));
        if (successMessage) {
          NotificationService.create({
            type: NotificationService.type.TOAST,
            subtype: NotificationService.subtype.SUCCESS,
            text: successMessage,
          });
        }
      })
      .catch((err) => parseCatch(err));
  };
}

function updateAutopayment(autoPaymentId, data, username, accessHash, successMessage) {
  return (dispatch) => {
    const url = ApiService.substitute(UPDATE_AUTOPAYMENT_URL, { autoPaymentId });
    ApiService.put(encodeURI(url), {
      data,
      headers: {
        Username: username,
        'Access-Hash': accessHash,
      },
      withCredentials: false,
    })
      .then((res) => {
        if (isValidResponse(res)) {
          dispatch(updatedAutopayment(res.data));
          if (successMessage) {
            NotificationService.create({
              type: NotificationService.type.TOAST,
              subtype: NotificationService.subtype.SUCCESS,
              text: successMessage,
            });
          }
        }
      })
      .catch((err) => parseCatch(err));
  };
}

function resetAutopayment() {
  return (dispatch) => {
    dispatch(deletedAutopayment(null));
  };
}

function setDefaultPaymentProcessor(user, processorId, successMessage) {
  const { username, accessHash, lastCashOut } = user;

  const data = { user: { activeProcessorId: processorId } };

  const headers = {
    Username: username,
    'Access-Hash': accessHash,
  };

  if (lastCashOut) {
    data.lastCashOut = true;
    headers.Jwt = accessHash;
  }

  return (dispatch) => {
    ApiService.post(PAYMENT_PROCESSORS_URL, {
      data,
      headers,
      withCredentials: false,
    })
      .then((res) => {
        if (isValidResponse(res)) {
          dispatch(changeDefaultProcessor(res.data.activeProcessorId));
          if (successMessage) {
            NotificationService.create({
              type: NotificationService.type.TOAST,
              subtype: NotificationService.subtype.SUCCESS,
              text: successMessage,
            });
          }
        } else {
          logger.warn(getApiWarnMsg('setDefaultPaymentProcessor', res.data));
        }
      })
      .catch((err) => parseCatch(err));
  };
}

function sendTwoFaCode(data) {
  return () => TwoFaService.validateCode(data);
}

function resendTwoFaCode(data) {
  return () => TwoFaService.resendCode(data);
}

function linkPaymentProcessorIsValid({
  response,
  successMessage,
  username,
  accessHash,
  processorId,
  lastCashOut,
  groupId,
}) {
  return (dispatch) => {
    if (response.valid) {
      NotificationService.create({
        type: NotificationService.type.TOAST,
        subtype: NotificationService.subtype.SUCCESS,
        text: successMessage,
      });
      dispatch(getPaymentProcessors(username, accessHash, lastCashOut));
    } else {
      dispatch(updateLinkingStatus(APIStates.INITIAL));
      if (groupId) {
        if (!response.status || response.status !== ENUM_TWO_FA.VALIDATION_REQUIRED) {
          NotificationService.create({
            type: NotificationService.type.TOAST,
            text: translate('payment.v2.paymentSetup.otherProcessor.signupFailedOnBacked'),
            subtype: NotificationService.subtype.ERROR,
          });
        }
      } else if (processorId === ENUM_PAYMENT_PROCESSOR_IDS.SOCIALE) {
        dispatch(updateSociale(response));
      } else {
        dispatch(updateProcessorFields(processorId, response.fields));
      }
    }
  };
}

function linkPaymentProcessor(
  user,
  processorId,
  fields,
  successMessage,
  endpoint = PAYMENT_LINK_PROCESSOR_URL,
  groupId
) {
  const { username, accessHash, lastCashOut } = user;
  const data = {
    processorId,
    fields,
  };

  const headers = {
    Username: username,
    'Access-Hash': accessHash,
  };

  if (lastCashOut) {
    data.lastCashOut = true;
    headers.Jwt = accessHash;
  }

  return (dispatch) => {
    dispatch(updateLinkingStatus(APIStates.PENDING));

    return ApiService.post(endpoint, {
      data,
      headers,
      withCredentials: false,
    })
      .then((response) => {
        if (isValidResponse(response)) {
          dispatch(
            linkPaymentProcessorIsValid({
              response: response.data,
              successMessage,
              username,
              accessHash,
              processorId,
              lastCashOut,
              groupId,
            })
          );

          return response.data;
        }

        dispatch(updateLinkingStatus(APIStates.INITIAL));
        logger.warn(getApiWarnMsg('linkPaymentProcessor', response.data));

        return undefined;
      })
      .catch((err) => {
        dispatch(updateLinkingStatus(APIStates.INITIAL));
        parseCatch(err);
      });
  };
}

function updatePaymentProcessor(user, accountId, processorId, fields, successMessage) {
  const { username, accessHash, lastCashOut } = user;
  const data = { fields };

  const headers = {
    Username: username,
    'Access-Hash': accessHash,
  };

  if (lastCashOut) {
    data.lastCashOut = true;
    headers.Jwt = accessHash;
  }

  return (dispatch) => {
    const url = ApiService.substitute(PAYMENT_UPDATE_PROCESSOR_URL, { accountId });

    return ApiService.patch(encodeURI(url), {
      data,
      headers,
      withCredentials: false,
    })
      .then((res) => {
        if (isValidResponse(res)) {
          if (res.data.valid && res.data.status !== 'VALIDATION_REQUIRED') {
            dispatch(getPaymentProcessors(username, accessHash, lastCashOut));
            NotificationService.create({
              type: NotificationService.type.TOAST,
              subtype: NotificationService.subtype.SUCCESS,
              text: successMessage,
            });

            return undefined;
          }

          if (res.data.status === 'VALIDATION_REQUIRED') {
            return res.data;
          }

          dispatch(updateProcessorFields(processorId, res.data.fields));

          return undefined;
        }

        logger.warn(getApiWarnMsg('updatePaymentProcessor', res.data));

        return undefined;
      })
      .catch((err) => parseCatch(err));
  };
}

function deletePaymentProcessor(user, accountId) {
  const { username, accessHash, lastCashOut } = user;

  return (dispatch) => {
    let url = ApiService.substitute(PAYMENT_DELETE_PROCESSOR_URL, { accountId });

    const headers = {
      Username: username,
      'Access-Hash': accessHash,
    };

    if (lastCashOut) {
      url += '?lastCashOut=true';
      headers.Jwt = accessHash;
    }

    return ApiService.delete(encodeURI(url), {
      headers,
      withCredentials: false,
    })
      .then(() => {
        dispatch(getPaymentProcessors(username, accessHash, lastCashOut));
      })
      .catch((err) => parseCatch(err));
  };
}

function deleteBitSafePaymentProcessor(user, accountId) {
  const { username, accessHash, lastCashOut } = user;

  let url = ApiService.substitute(PAYMENT_DELETE_PROCESSOR_URL, { accountId });

  const headers = {
    Username: username,
    'Access-Hash': accessHash,
  };

  if (lastCashOut) {
    url += '?lastCashOut=true';
    headers.Jwt = accessHash;
  }

  return async(dispatch) => {
    await ApiService.delete(encodeURI(url), {
      headers,
      withCredentials: false,
    });

    return dispatch(getPaymentProcessors(username, accessHash, lastCashOut));
  };
}

export const selector = createStructuredSelector({
  products,
  memberships,
  isFetchingProducts,
  isFetchingMemberships,
  isFetchingPayementMethods,
  paymentTypes,
  isBuyTokensModalVisible,
  isUpgradeToGoldModalVisible,
  payouts,
  requests,
  autoPayment,
  membershipOptions,
  isRegisterGoldOverlayVisible,
  isUpsellModalVisible,
  sociale,
  linkingStatus,
});

export const actionCreators = {
  showBuyTokensModal,
  showUpgradeToGoldModal,
  hideBuyTokensModal,
  hideUpgradeToGoldModal,
  fetchPaymentMethods,
  fetchGoldMemberships,
  getPaymentProcessors,
  setDefaultPaymentProcessor,
  linkPaymentProcessor,
  linkPaymentProcessorIsValid,
  updatePaymentProcessor,
  deletePaymentProcessor,
  deleteBitSafePaymentProcessor,
  getAutoPaymentData,
  createAutopayment,
  deleteAutopayment,
  resetAutopayment,
  updateAutopayment,
  sendTwoFaCode,
  resendTwoFaCode,
  setMembershipOptions,
  toggleRegisterGoldOverlay,
  toggleUpsellModal,
};
